import React, { useState } from 'react';
import * as ReactDOM from 'react-dom';
import { useForm } from 'react-hook-form';
import { useDispatch } from 'react-redux';
import { Button, Icons, Row } from '../../../components/base/BootstrapComponents/BootstrapComponents';
import { USER_TYPE, USER_TYPE_LIST } from '../../../configs';
import FieldDTOWithCol from '../../../components/fields/Abstract/FieldDTOWithCol';
import TextInputWithLabel from '../../../components/fields/TextInput/TextInputWithLabel';
import SZLTDTO from '../../../dto/SZLTDTO';
import { createSZLT, editSZLTById, loadSZLTFieldsValidation } from '../actions';
import { errorNotification, successNotification } from '../../Notifications/notificationDecorator';
import {
  getResponseError, quotesTransform, responseLogWithNotification, setFormError,
} from '../../../utils/utils';
import ApplicantForm from '../../Application/components/FormBlocks/ApplicantForm';
import DatePicker from '../../../components/fields/DatePicker/DatePicker';
import { showGlobalConfirm } from '../../System/actions';
import { infoFormatterIntl, renderRegistryValidationMessage } from '../../../utils/formatters';
import AddressInput from '../../../components/fields/Address/AddressInput';

export default function SZLTForm(props) {
  const {
    onClose = () => {},
    loadData = () => {},
    data = {},
    isEdit = false,
    formMeta,
  } = props;
  const dispatch = useDispatch();
  const [applicantType, setApplicantType] = useState(data && data.legal_entity_type ? data.legal_entity_type : USER_TYPE.LEGAL);
  const {
    watch,
    control,
    getValues,
    setValue,
    formState: { isDirty, dirtyFields, errors },
    setError,
    handleSubmit,
  } = useForm({
    mode: 'onChange',
    defaultValues: {
      legal_entity_type: applicantType,
      ...data,
    },
  });
  React.useEffect(() => {
    const subscription = watch(values => setApplicantType(values.legal_entity_type));
    return () => subscription.unsubscribe();
  }, [watch]);

  const handleValidateAndSave = async () => {
    if (!data?.id) {
      return handleSave();
    }
    const values = getValues();
    dispatch(loadSZLTFieldsValidation({ id: data.id, data: { input_data: values } })).then(data => {
      const checkErrors = data?.checks || {};
      const errors = data?.errors || {};
      const checkErrorsKeys = Object.keys(checkErrors);
      const errorsKeys = Object.keys(errors);
      if (Object.values(checkErrors).filter(f => f.status !== 2).length + errorsKeys.length) {
        dispatch(showGlobalConfirm({
          confirmTitle: 'Реестровая запись содержит ошибки в полях',
          confirmMessage: renderRegistryValidationMessage(errors, checkErrors, checkErrorsKeys, errorsKeys),
          confirmButtonText: 'Сохранить',
          confirmClassName: 'remove-preset',
        })).then(() => {
          handleSave();
        }).catch(() => {});
      } else {
        handleSave();
      }
    }).catch(responseLogWithNotification);
  };

  const handleSave = async () => {
    const keys = Object.keys(errors);
    if (errors && keys.length) {
      errorNotification('Сохранение формы СЗЛТ', 'Ошибка заполнения формы');
      const { ref } = errors[keys[0]];
      ref.focus();
      setError('number', { type: 'custom', message: 'custom message' });
      return null;
    }
    const action = isEdit ? editSZLTById : createSZLT;
    const values = getValues();
    dispatch(action(values))
      .then(() => {
        loadData();
        onClose();
        successNotification('Сохранение формы СЗЛТ', 'Сформировано Решение о внесении изменений');
      })
      .catch(response => {
        setFormError(response, setError);
        errorNotification('Сохранение формы СЗЛТ', getResponseError({ error: response && response.message ? response.message : '' }));
      });
  };

  const DTO = new SZLTDTO();
  const attributes = DTO.getAttributesByName();

  return ReactDOM.createPortal(
    <div className="application-form">
      <div className="application-form-header">
        <div className="breadcrumbs">
          <span className="breadcrumbs-prev">Реестры</span>
          <Icons width={22} height={22} customIcon icon="arrow-right" className="breadcrumbs-arrow" />
          <span className="breadcrumbs-prev">Реестр СЗЛТ</span>
          <Icons width={22} height={22} customIcon icon="arrow-right" className="breadcrumbs-arrow" />
          <span className="breadcrumbs-prev"> Карточка реестра СЗЛТ</span>
          <Icons width={22} height={22} customIcon icon="arrow-right" className="breadcrumbs-arrow" />
          <span className="breadcrumbs-current">Карточка реестра СЗЛТ № {data?.number || 'Без номера'} ({isEdit ? 'редактирование' : 'создание'})
          </span>
        </div>
        <div className="close-button" onClick={onClose}>
          <Icons width={24} height={24} customIcon icon="close" />
        </div>
      </div>
      <div className="application-form-body">
        <form className="application-form-content small" onSubmit={handleSubmit(handleValidateAndSave)}>
          <div className="application-form-fields-header">Заполните информацию о документе</div>
          <div className="application-form-fields">
            <div className="application-form-fields-wrapper custom-scroll-theme">
              <Row className="row-cols-3">
                <FieldDTOWithCol
                  control={control}
                  formMeta={formMeta}
                  type="text"
                  component={TextInputWithLabel}
                  dtoObject={attributes.number}
                  variant="material"
                  disabled
                />
                <FieldDTOWithCol
                  control={control}
                  formMeta={formMeta}
                  type="text"
                  component={DatePicker}
                  dtoObject={attributes.effective_date}
                  withLabel
                  changeFormat="YYYY-MM-DD"
                  variant="material"
                />
                <FieldDTOWithCol
                  control={control}
                  formMeta={formMeta}
                  type="text"
                  component={DatePicker}
                  changeFormat="YYYY-MM-DD"
                  dtoObject={attributes.expiration_date}
                  withLabel
                  variant="material"
                />
              </Row>
              <div className="f4 mt-3">СЗЛТ</div>
              <ApplicantForm
                watch={watch}
                control={control}
                formMeta={formMeta}
                setValue={setValue}
                getValues={getValues}
                applicantType={applicantType}
                hideFileInput
                availableTypes={USER_TYPE_LIST.filter(f => f.value !== USER_TYPE.PRIVATE_PERSON && f.value !== USER_TYPE.PRIVATE_PERSON_OLD)}
              />
              {/* <Row className="row-cols-2"> */}
              {/*  <FieldDTOWithCol */}
              {/*    control={control} */}
              {/*    formMeta={formMeta} */}
              {/*    type="text" */}
              {/*    component={TextInputWithLabel} */}
              {/*    dtoObject={attributes.commercial_name} */}
              {/*    variant="material" */}
              {/*    transform={quotesTransform} */}
              {/*  /> */}
              {/*  <FieldDTOWithCol */}
              {/*    control={control} */}
              {/*    formMeta={formMeta} */}
              {/*    type="text" */}
              {/*    component={TextInputWithLabel} */}
              {/*    dtoObject={attributes.logo_files} */}
              {/*    variant="material" */}
              {/*    transform={quotesTransform} */}
              {/*  /> */}
              {/* </Row> */}
              <div className="f4 mt-3">Дополнительная информация</div>
              <Row className="row-cols-2">
                <FieldDTOWithCol
                  control={control}
                  formMeta={formMeta}
                  type="text"
                  component={TextInputWithLabel}
                  dtoObject={attributes.decision}
                  variant="material"
                />
                <FieldDTOWithCol
                  control={control}
                  formMeta={formMeta}
                  type="text"
                  component={DatePicker}
                  changeFormat="YYYY-MM-DD"
                  dtoObject={attributes.decision_date}
                  variant="material"
                  withLabel
                />
              </Row>
              <Row className="row-cols-3">
                <FieldDTOWithCol
                  control={control}
                  formMeta={formMeta}
                  type="text"
                  component={TextInputWithLabel}
                  dtoObject={attributes.office_phone}
                  variant="material"
                />
                <FieldDTOWithCol
                  control={control}
                  formMeta={formMeta}
                  type="text"
                  component={TextInputWithLabel}
                  dtoObject={attributes.office_email}
                  variant="material"
                />
                <FieldDTOWithCol
                  control={control}
                  formMeta={formMeta}
                  type="text"
                  component={TextInputWithLabel}
                  dtoObject={attributes.web_site}
                  variant="material"
                />
              </Row>
              <Row className="row-cols-1">
                <FieldDTOWithCol
                  control={control}
                  formMeta={formMeta}
                  type="text"
                  component={TextInputWithLabel}
                  dtoObject={attributes.order_receiving_methods}
                  variant="material"
                />
              </Row>
              <Row className="row-cols-1">
                <FieldDTOWithCol
                  control={control}
                  formMeta={formMeta}
                  type="text"
                  component={AddressInput}
                  dtoObject={attributes.office_address}
                  variant="material"
                />
              </Row>
              <Row className="row-cols-1">
                <FieldDTOWithCol
                  control={control}
                  formMeta={formMeta}
                  type="text"
                  component={TextInputWithLabel}
                  dtoObject={attributes.software_names}
                  variant="material"
                />
              </Row>
              <Row className="row-cols-1">
                <FieldDTOWithCol
                  control={control}
                  formMeta={formMeta}
                  type="text"
                  component={AddressInput}
                  dtoObject={attributes.database_address}
                  variant="material"
                />
              </Row>
              <Row className="row-cols-1">
                <FieldDTOWithCol
                  control={control}
                  formMeta={formMeta}
                  type="text"
                  component={AddressInput}
                  dtoObject={attributes.server_address}
                  variant="material"
                />
              </Row>
            </div>
          </div>
          <div className="application-form-footer">
            <Button variant="secondary" onClick={() => onClose()}>Отменить</Button>
            <Button type="submit">{isEdit ? 'Сохранить' : 'Создать'}</Button>
          </div>
        </form>
      </div>
    </div>,
    document.getElementById('app-content'),
  );
}
